import { useState, useEffect } from 'react';
import AppLayout from '../components/layout/AppLayout';
import Seo from '../components/Seo';
import ToggleSwitch from '../components/settings/ToggleSwitch';
import ThemeSelector from '../components/settings/ThemeSelector';
import { useWallet } from '../context/WalletContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';

function truncate(addr) {
  if (!addr) return '';
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

export default function Settings() {
  const { t } = useTranslation('settings');
  const { walletAddress, preferences, savePreferences, disconnect } = useWallet();
  const { setThemeMode } = useTheme();

  const [email, setEmail] = useState('');
  const [weeklyReports, setWeeklyReports] = useState(false);
  const [txAlerts, setTxAlerts] = useState(false);
  const [hideSmall, setHideSmall] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!preferences) return;
    setEmail(preferences.email ?? '');
    setWeeklyReports(preferences.notif_weekly_reports ?? false);
    setTxAlerts(preferences.notif_tx_alerts ?? false);
    setHideSmall(preferences.hide_small_balances ?? true);
  }, [preferences]);

  async function handleSaveEmail() {
    setSaving(true);
    try { await savePreferences({ email }); }
    catch (err) { console.error('Save email error:', err); }
    finally { setSaving(false); }
  }

  async function handleToggle(field, value) {
    try { await savePreferences({ [field]: value }); }
    catch (err) { console.error('Save preference error:', err); }
  }

  async function handleThemeChange(mode) {
    setThemeMode(mode);
    if (walletAddress) {
      try { await savePreferences({ theme: mode }); }
      catch (err) { console.error('Save theme error:', err); }
    }
  }

  return (
    <AppLayout>
      <Seo
        title="Settings | G12 Labs"
        description="Your G12 Labs account settings."
        path="/settings"
        noindex
      />
      <div className="settings-page">
        <h1 className="settings-page-title">{t('title')}</h1>

        {/* ── Wallet ── */}
        <div className="settings-card">
          <div className="settings-card-header">
            <i className="ph-bold ph-wallet" />
            <div>
              <h3>{t('wallet.title')}</h3>
              <p>{t('wallet.sub')}</p>
            </div>
          </div>

          <div className="settings-row">
            <div className="settings-row-info">
              <span className="settings-row-label">{t('wallet.connectedWallet')}</span>
              <span className="settings-row-sub">{t('wallet.connectedWalletSub')}</span>
            </div>
            {walletAddress ? (
              <div className="app-wallet-bubble">
                <div className="app-wallet-dot" />
                <span>{truncate(walletAddress)}</span>
              </div>
            ) : (
              <span style={{ color: 'var(--text-subtle)', fontSize: 13 }}>{t('wallet.noWallet')}</span>
            )}
          </div>

          <div className="settings-row">
            <div className="settings-row-info">
              <span className="settings-row-label">{t('wallet.activeNetwork')}</span>
              <span className="settings-row-sub">{t('wallet.activeNetworkSub')}</span>
            </div>
            <div className="settings-network-badge">
              <div className="settings-network-dot" />
              {t('wallet.network')}
            </div>
          </div>

          <div className="settings-row">
            <div className="settings-row-info">
              <span className="settings-row-label">{t('wallet.disconnect')}</span>
              <span className="settings-row-sub">{t('wallet.disconnectSub')}</span>
            </div>
            <button className="settings-btn-danger" onClick={disconnect} disabled={!walletAddress}>
              <i className="ph-bold ph-sign-out" /> {t('wallet.disconnect')}
            </button>
          </div>
        </div>

        {/* ── Appearance ── */}
        <div className="settings-card">
          <div className="settings-card-header">
            <i className="ph-bold ph-palette" style={{ color: 'var(--eth)' }} />
            <div>
              <h3>{t('appearance.title')}</h3>
              <p>{t('appearance.sub')}</p>
            </div>
          </div>

          <div className="settings-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 16 }}>
            <div className="settings-row-info" style={{ width: '100%' }}>
              <span className="settings-row-label">{t('appearance.colorMode')}</span>
              <span className="settings-row-sub">{t('appearance.colorModeSub')}</span>
            </div>
            <ThemeSelector onThemeChange={handleThemeChange} />
          </div>

          <div className="settings-row">
            <div className="settings-row-info">
              <span className="settings-row-label">{t('appearance.hideSmall')}</span>
              <span className="settings-row-sub">{t('appearance.hideSmallSub')}</span>
            </div>
            <ToggleSwitch
              checked={hideSmall}
              onChange={e => { setHideSmall(e.target.checked); handleToggle('hide_small_balances', e.target.checked); }}
            />
          </div>
        </div>

        {/* ── Notifications ── */}
        <div className="settings-card">
          <div className="settings-card-header">
            <i className="ph-bold ph-bell-ringing" style={{ color: 'var(--success)' }} />
            <div>
              <h3>{t('notifications.title')}</h3>
              <p>{t('notifications.sub')}</p>
            </div>
          </div>

          <div className="settings-row">
            <div className="settings-row-info">
              <span className="settings-row-label">{t('notifications.email')}</span>
              <span className="settings-row-sub">{t('notifications.emailSub')}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="email"
                className="settings-input"
                placeholder={t('notifications.emailPlaceholder')}
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
              <button className="settings-btn-secondary" onClick={handleSaveEmail} disabled={saving}>
                {saving ? t('notifications.saving') : t('notifications.save')}
              </button>
            </div>
          </div>

          <div className="settings-row">
            <div className="settings-row-info">
              <span className="settings-row-label">{t('notifications.weeklyReports')}</span>
              <span className="settings-row-sub">{t('notifications.weeklyReportsSub')}</span>
            </div>
            <ToggleSwitch
              checked={weeklyReports}
              onChange={e => { setWeeklyReports(e.target.checked); handleToggle('notif_weekly_reports', e.target.checked); }}
            />
          </div>

          <div className="settings-row">
            <div className="settings-row-info">
              <span className="settings-row-label">{t('notifications.txAlerts')}</span>
              <span className="settings-row-sub">{t('notifications.txAlertsSub')}</span>
            </div>
            <ToggleSwitch
              checked={txAlerts}
              onChange={e => { setTxAlerts(e.target.checked); handleToggle('notif_tx_alerts', e.target.checked); }}
            />
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
